/**
 * Trunnion AI - Audit Filter Bar
 * Date range, event type, agent, and classification filters.
 */

import { Search, Calendar, Filter } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CLASSIFICATION_LEVELS } from '@/config/constants';
import type { AuditLogFilters } from '@/types';

interface AuditFilterBarProps {
  filters: AuditLogFilters;
  onChange: (filters: AuditLogFilters) => void;
}

const eventTypeOptions = [
  { value: '', label: 'All Event Types' },
  { value: 'workflow.executed', label: 'Workflow Executed' },
  { value: 'execution.started', label: 'Execution Started' },
  { value: 'execution.completed', label: 'Execution Completed' },
  { value: 'execution.failed', label: 'Execution Failed' },
  { value: 'tool.called', label: 'Tool Called' },
  { value: 'tool.denied', label: 'Tool Denied' },
  { value: 'policy.violated', label: 'Policy Violated' },
  { value: 'auth.login', label: 'Login' },
  { value: 'auth.logout', label: 'Logout' },
  { value: 'classification.violation', label: 'Classification Violation' },
];

export function AuditFilterBar({ filters, onChange }: AuditFilterBarProps) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 p-4">
      <div className="flex-1 min-w-50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-trunnion-slate-400" />
          <input
            type="text"
            value={filters.searchQuery ?? ''}
            onChange={(e) =>
              onChange({ ...filters, searchQuery: e.target.value })
            }
            placeholder="Search audit events..."
            className="w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 py-2 pl-9 pr-3 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500"
          />
        </div>
      </div>

      <Select
        options={eventTypeOptions}
        value={filters.eventTypes?.[0] ?? ''}
        onChange={(e) =>
          onChange({
            ...filters,
            eventTypes: e.target.value
              ? ([e.target.value] as AuditLogFilters['eventTypes'])
              : undefined,
          })
        }
      />

      <Select
        options={[
          { value: '', label: 'All Classifications' },
          ...CLASSIFICATION_LEVELS.map((l) => ({ value: l, label: l })),
        ]}
        value={filters.classification ?? ''}
        onChange={(e) =>
          onChange({
            ...filters,
            classification: e.target.value
              ? (e.target.value as AuditLogFilters['classification'])
              : undefined,
          })
        }
      />

      <div className="flex items-center gap-2">
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-trunnion-slate-400" />
          <Input
            type="date"
            value={filters.startDate ?? ''}
            onChange={(e) =>
              onChange({ ...filters, startDate: e.target.value })
            }
            className="pl-9"
          />
        </div>
        <span className="text-trunnion-slate-400">to</span>
        <Input
          type="date"
          value={filters.endDate ?? ''}
          onChange={(e) =>
            onChange({ ...filters, endDate: e.target.value })
          }
        />
      </div>

      <button
        onClick={() =>
          onChange({ page: 1, pageSize: filters.pageSize })
        }
        className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-trunnion-slate-400 hover:text-white hover:bg-trunnion-slate-700 transition-colors"
      >
        <Filter className="h-4 w-4" />
        Reset
      </button>
    </div>
  );
}
